import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { t } from '@/i18n';

export const KpiCard = ({ label, value, subtitle, trend, variant = 'default', target, onClick }) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3" />;
    if (trend === 'down') return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  // OEE donut variant
  if (variant === 'oee') {
    const numVal = typeof value === 'string' ? parseFloat(value) : value;
    const donutData = [
      { value: numVal },
      { value: 100 - numVal },
    ];
    const color = numVal >= 80 ? '#16a34a' : numVal >= 60 ? '#ca8a04' : '#dc2626';

    return (
      <Card
        className={cn('relative overflow-hidden group cursor-pointer transition-all hover:shadow-lg min-h-[140px] h-full', onClick && 'hover:ring-2 hover:ring-primary/20')}
        onClick={onClick}
      >
        <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-primary/40 to-primary/60" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        <CardHeader className="pb-2 pt-5 relative">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
        </CardHeader>
        <CardContent className="relative flex items-center gap-3 min-w-0">
          <div className="w-16 h-16 relative shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  innerRadius={22}
                  outerRadius={30}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  stroke="none"
                >
                  <Cell fill={color} />
                  <Cell fill="#e5e7eb" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{numVal}%</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-muted-foreground whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{t('kpi.target')}: {target}%</div>
            {subtitle && (
              <div className={cn(
                'flex items-center gap-1 text-xs font-semibold mt-1 whitespace-nowrap',
                trend === 'up' && 'text-green-600',
                trend === 'down' && 'text-red-600',
                trend === 'neutral' && 'text-muted-foreground',
              )}>
                {getTrendIcon()}
                <span>{subtitle}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'relative overflow-hidden group transition-all min-h-[140px] h-full',
        variant === 'energy' && 'border-l-4 border-l-green-500',
        onClick && 'cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-primary/20',
      )}
      onClick={onClick}
    >
      <div className={cn(
        'absolute top-0 left-0 right-0 h-1 rounded-t-2xl',
        trend === 'up' && 'bg-gradient-to-r from-green-400 to-green-500',
        trend === 'down' && 'bg-gradient-to-r from-red-400 to-red-500',
        trend === 'neutral' && 'bg-gradient-to-r from-primary/40 to-primary/60',
        variant === 'energy' && 'bg-gradient-to-r from-green-400 to-emerald-500',
      )} />

      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

      <CardHeader className="pb-2 pt-5 relative">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p className="text-sm font-medium text-muted-foreground truncate">{label}</p>
          {trend && subtitle && (
            <div className={cn(
              'flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 whitespace-nowrap',
              trend === 'up' && 'bg-green-100 text-green-700',
              trend === 'down' && 'bg-red-100 text-red-700',
              trend === 'neutral' && 'bg-muted text-muted-foreground',
            )}>
              {getTrendIcon()}
              <span>{subtitle}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="relative">
        <div className="min-w-0">
          <div className="text-2xl font-extrabold tracking-tight whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
          {target && (
            <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{t('kpi.target')}: {target}</div>
          )}
        </div>
        {variant === 'energy' && (
          <Badge variant="success" className="mt-2 bg-green-100 text-green-700 hover:bg-green-100 whitespace-nowrap">
            {t('kpi.offPeakUntil14')}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};
